import { afterNextRender, Injectable } from '@angular/core'
import { KeycloakService } from 'keycloak-angular'
import { PlatformService } from './platform.service'
import { CodeIntegrityVerifier } from './auth-protection'
import * as jose from 'jose'
import { v4 as uuidv4 } from 'uuid'
import {AuthState} from '@chatize-ui/schemes/components/auth-state'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Private key for JWT signing
  private readonly PRIVATE_KEY = `-----BEGIN PRIVATE KEY-----
MIIEvAIBADANBgkqhkiG9w0BAQEFAASCBKYwggSiAgEAAoIBAQCWhOZeq4BbpwMw
AvVS/itCs1EZ5APd/FxEs1Vd2HKCHpagIHbltOzK+Umphc91BzFA2CIuHHkRkFL7
CpncVvuG6y0AH/sJ/Jd+piIwyIGKiyVSOXBOVJONd7H+Gq3DHMw8e5owBwsdQAuz
YrB/EZyIron9UHPVfJ/xJL1dBNdB+ND4f1Cb8+MEuX2VORw//sXT5sUOBvx1KAzV
H3bTCMIhDS5xfeSt1pE0FZ3KHIe0g4AG1FJpqcbcAVltC6H4BN6S9a5PH89Cyxtp
tsB8pe4zA/BXL7d+lscuYbBTpHaCmjwI2UUHvGrEv0NNw0b6jMn+7gAMbRIcqxX2
ujOHzEjJAgMBAAECggEAC/X6onTpacm/Q0OblWE/ic4wYyjPNTGBGYUmAvjuEdtM
Eo8R3lYUElsLQ06FT6I8g+UeQ4tyrsvJCaGW3BSzac4VZjL5uBpGOu5eaCxUEvDU
tzsmQACQ4HHYXJNn0cnfjEIxh0sqqG6MSiXNAFlOFunGawacyyRbR1vT375AkWw3
i/B2nxvMKgKMxD9dyy/MYpRm5Dn3sf8Pg5rR2QKwtcKyfzqukA5fC/IRtH+YS9ca
B/dbOlhGcXb4tl/ufuzGgvPVX2e9wgK4m5Jrwj2WU4AJ6aEUfEVVlP50ZomBgri7
LjYylb9wLALv8bIKqnZBPLYwORys0k0G0+AGzFWrSwKBgQC8P3V7Ie99snB9yzhQ
v4SqdBC17wijxhFOzV7s1xBgrqBYjphiu0xfd7pT/WslOhfR2C7ukDf5a6ca8in9
dKLR3gNGPXBEgMzt5xuRcaKWETHihwfrDusGA8SJLm6iKQjGfGr1YYj9CmEgvzJ5
FOvn+JtOK31xrs8oc9T42dMH4wKBgQDMsT0HEn0pEqyNcbHY4uJbStLuS2F7pPMx
e6v8UqwxewpSKpMZMOHJWBZrphtFKM8hedhQpulJz1og/D8jCp2+0rw8eMMCHUL1
Zlb0UT/LqZkYA1gnq5PF97oHi9JCnRTNc6dZ4nt2qzbBI10dV55bocKepMPZAnvU
CVzTwquUYwKBgB0TxEbHwCFvTZbwq/s1Cf32PZqMmI4LiyLb8ByvP5Vfg98J75nY
inx4luxgSYuPhJxiQyWQW0UIjuBHpZrDyF6YZ3MTDGNYbc8VkpmNrup1L3ppFx0E
yeK89D+oT2h25CcckrziWvGTB9uyDM7NH39OVFbnutGzicYa1ju2EbW3AoGABkpU
E8mPqxl10Ar0ajPevOt5u/T5E/Gnv5eLW3+25vZfBNNmHdoQtj1MwxYhtGhipSa3
bRMUDD6hCG54Cj2jR1/2hO05IPGBQ0VX2r48dafKmqBPrzhonCbWwrCbT4zAPOgW
9n4tSSJN1nWdwO8WYL6B2po5ooue/o8jGSbcWv0CgYBtc4/jN+rYo07mvJibJFfK
84mpxeVBXVFiHGUYlTCIUBEOF3K0mT1Cs2Zc6IL4MKtisMSa/IS2rqHBuKOOO+dY
6JZr2N8GF+ZYC/eGH4idCkypUiZ4BVu7kIWRc1GsHA95q6H0Y3FVB8YHh1X6sUsH
xJ2bisBMhfXl3t24RowDTw==
-----END PRIVATE KEY-----`

  private readonly CLIENT_ID_STORAGE_KEY = 'chatize_client_id'
  private readonly CLIENT_ID_CREATED_KEY = 'chatize_client_id_created'
  private readonly TOKEN_STORAGE_KEY = 'chatize_client_token'
  private readonly TOKEN_CREATED_KEY = 'chatize_client_token_created'
  private readonly TOKEN_EXPIRY_HOURS = 1
  
  private _state: AuthState

  constructor(
    private platformService: PlatformService,
    private keycloakService: KeycloakService,
  ) {
  }

  initState(): void {
    if (this.platformService.isServer) return
    
    // Verify code integrity before proceeding
    CodeIntegrityVerifier.verifyIntegrity();
    
    afterNextRender(() => {
      try {
        this._state = this.keycloakService.isLoggedIn() ? AuthState.LoggedIn : AuthState.NotLoggedIn
      } catch (error) {
        console.warn('Keycloak authentication failed (likely due to CSP/network issues), defaulting to anonymous mode:', error)
        this._state = AuthState.NotLoggedIn
      }
    })
  }

  login(): void {
    this.keycloakService.login()
  }

  get state(): AuthState {
    return this._state
  }
  
  /**
   * Get the authentication token based on user login state
   * If user is logged in, returns the Keycloak token
   * Otherwise, generates and returns a custom JWT token
   */
  async getAuthToken(): Promise<{ token: string; clientId?: string }> {
    // If user is logged in, use Keycloak token
    if (this.state === AuthState.LoggedIn) {
      const token = await this.getKeycloakToken();
      // Get the client ID in case this user was previously anonymous
      const clientId = this.platformService.isBrowser ? localStorage.getItem(this.CLIENT_ID_STORAGE_KEY) : null;
      return { token, clientId };
    } 
    // Otherwise, use custom JWT token
    else {
      const token = await this.getOrCreateAnonymousToken();
      const clientId = this.platformService.isBrowser ? localStorage.getItem(this.CLIENT_ID_STORAGE_KEY) : null;
      return { token, clientId };
    }
  }

  /**
   * Get the Keycloak token for authenticated users
   */
  private async getKeycloakToken(): Promise<string> {
    return this.keycloakService.getToken();
  }

  /**
   * Get an existing anonymous JWT token or create a new one
   */
  private async getOrCreateAnonymousToken(): Promise<string> {
    // Skip token generation in server-side rendering
    if (this.platformService.isServer) {
      return '';
    }

    // Check if we have a valid token in storage
    const storedToken = localStorage.getItem(this.TOKEN_STORAGE_KEY);
    const tokenCreated = localStorage.getItem(this.TOKEN_CREATED_KEY);
    
    if (storedToken && tokenCreated) {
      const createdDate = new Date(tokenCreated);
      const expiryDate = new Date(createdDate.getTime() + this.TOKEN_EXPIRY_HOURS * 60 * 60 * 1000);
      
      // If token is still valid (not expired), return it
      // Add a buffer time (5 minutes) to refresh token before it actually expires
      const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
      if (expiryDate > new Date(Date.now() + bufferTime)) {
        return storedToken;
      }
    }
    
    // Generate a new token
    return this.generateAnonymousToken();
  }

  /**
   * Get or create a unique client ID for anonymous users
   * ClientId is persistent to maintain access to uploaded resources
   * Backend auto-deletes resources after 24 hours anyway
   */
  private getOrCreateClientId(): string {
    if (this.platformService.isServer) {
      return 'server-side';
    }

    let clientId = localStorage.getItem(this.CLIENT_ID_STORAGE_KEY);
    
    // Only create a new clientId if one doesn't exist
    if (!clientId) {
      // Generate new clientId
      clientId = uuidv4();
      localStorage.setItem(this.CLIENT_ID_STORAGE_KEY, clientId);
      localStorage.setItem(this.CLIENT_ID_CREATED_KEY, new Date().toISOString());
    }
    
    return clientId;
  }

  /**
   * Generate a new JWT token for anonymous users using RS256 algorithm
   * Token is valid for 1 hour
   */
  private async generateAnonymousToken(): Promise<string> {
    try {
      // Get or create client ID
      const clientId = this.getOrCreateClientId();
      
      // Import the private key directly (no need for Base64 conversion)
      const privateKey = await jose.importPKCS8(
        this.PRIVATE_KEY,
        'RS256'
      );
      
      // Set expiration time (1 hour from now)
      const now = new Date();
      const expirationTime = new Date(now.getTime() + this.TOKEN_EXPIRY_HOURS * 60 * 60 * 1000); // 1 hour
      
      // Create and sign the token using jose
      const token = await new jose.SignJWT({
        sub: clientId,
        aud: 'anonymous'
      })
        .setProtectedHeader({ alg: 'RS256' })
        .setIssuedAt()
        .setIssuer('chatize.com')
        .setExpirationTime(expirationTime)
        .sign(privateKey);
      
      // Store token and creation time
      localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
      localStorage.setItem(this.TOKEN_CREATED_KEY, now.toISOString());
      
      return token;
    } catch (error) {
      console.error('Error generating chatize client token:', error);
      return '';
    }
  }
}
